import { dateTimeFormat, numberFormat } from '../../../lib/format';
import { dashboardT } from '../../../lib/messages';
import { reportDimensionDefinitions, reportMetricDefinitions } from './catalog';
import type { ReportDimension } from './reportEnums';
import { ReportMetric, ReportMetricFormat } from './reportEnums';

export function formatReportMetricValue(value: unknown, metric: ReportMetric) {
  if (value === null || value === undefined || value === '') return '-';
  if (metric === ReportMetric.Day) return formatReportDay(value);
  if (metric === ReportMetric.Hour) return formatReportHour(value);
  const definition = reportMetricDefinitions[metric];
  if (definition.format === ReportMetricFormat.String) return scalarDisplayValue(value);
  if (definition.format === ReportMetricFormat.DateTime) return formatDateTime(value);

  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) return scalarDisplayValue(value);

  switch (definition.format) {
    case ReportMetricFormat.Currency:
      return numberFormat({
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 2,
      }).format(numeric);
    case ReportMetricFormat.Percentage:
      return numberFormat({
        style: 'percent',
        maximumFractionDigits: 2,
      }).format(numeric);
    case ReportMetricFormat.PercentagePlain:
      return `${numberFormat({ maximumFractionDigits: 2 }).format(numeric)}%`;
    case ReportMetricFormat.Duration:
      return `${numberFormat({ maximumFractionDigits: 2 }).format(numeric)} ms`;
    case ReportMetricFormat.BidFloor:
      return numberFormat({
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      }).format(numeric);
    case ReportMetricFormat.Decimal:
      return numberFormat({ maximumFractionDigits: 4 }).format(numeric);
    case ReportMetricFormat.Number:
    default:
      return numberFormat({ maximumFractionDigits: 2 }).format(numeric);
  }
}

export function formatReportChartAxisValue(value: number, metric: ReportMetric) {
  const definition = reportMetricDefinitions[metric];
  if (Math.abs(value) < 1_000) return formatReportMetricValue(value, metric);

  switch (definition.format) {
    case ReportMetricFormat.Currency:
    case ReportMetricFormat.BidFloor:
      return compactNumericValue(value, { style: 'currency', currency: 'USD' });
    case ReportMetricFormat.Number:
    case ReportMetricFormat.Decimal:
      return compactNumericValue(value);
    case ReportMetricFormat.Duration:
      return `${compactNumericValue(value)} ms`;
    default:
      return formatReportMetricValue(value, metric);
  }
}

function formatReportDay(value: unknown) {
  const scalar = scalarDisplayValue(value);
  const dateParts = /^(\d{4})-(\d{2})-(\d{2})/.exec(scalar);
  const [, year, month, day] = dateParts ?? [];
  if (year && month && day) return `${month}/${day}/${year}`;

  const date = new Date(scalar);
  return Number.isNaN(date.getTime())
    ? scalar
    : dateTimeFormat({
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        timeZone: 'UTC',
      }).format(date);
}

function formatReportHour(value: unknown) {
  const scalar = scalarDisplayValue(value);
  const hourParts = /^(\d{1,2})(?::(\d{2}))?(?::\d{2})?$/.exec(scalar);
  if (hourParts) {
    const hour = Number(hourParts[1]);
    if (hour >= 0 && hour <= 23) return `${String(hour).padStart(2, '0')}:${hourParts[2] ?? '00'}`;
  }

  const date = new Date(scalar);
  return Number.isNaN(date.getTime())
    ? scalar
    : `${String(date.getUTCHours()).padStart(2, '0')}:${String(date.getUTCMinutes()).padStart(2, '0')}`;
}

export function numericReportValue(value: unknown) {
  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : undefined;
}

export function reportMetricLabel(metric: ReportMetric) {
  return dashboardT(reportMetricDefinitions[metric].labelKey);
}

export function reportDimensionLabel(dimension: ReportDimension) {
  return dashboardT(reportDimensionDefinitions[dimension].labelKey);
}

export function humanizeReportIdentifier(value: string) {
  const acronyms = new Set(['dp', 'ivt', 'rpm', 'ctr', 'cpm', 'xp', 'id', 'url']);
  return value
    .toLowerCase()
    .split('_')
    .map((part) =>
      acronyms.has(part) ? part.toUpperCase() : `${part[0]?.toUpperCase() ?? ''}${part.slice(1)}`,
    )
    .join(' ');
}

function formatDateTime(value: unknown) {
  const scalar = scalarDisplayValue(value);
  const date = new Date(scalar);
  return Number.isNaN(date.getTime())
    ? scalar
    : dateTimeFormat({ dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

function scalarDisplayValue(value: unknown) {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return value.toString();
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return '-';
}

function compactNumericValue(
  value: number,
  options: Pick<Intl.NumberFormatOptions, 'style' | 'currency'> = {},
) {
  return numberFormat({
    ...options,
    notation: 'compact',
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(value);
}
